"use client";
import { useReducedMotion } from "@/hooks/useReducedMotion";
/**
 * AuraGlass Meta-Engine
 * Self-Evolving Glass Framework that analyzes its own usage and optimizes in real-time
 * Part of Next-Wave Systems (10/10) - Meta-Systems Framework
 */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  createContext,
  useContext,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";
import { ContrastGuard } from "../accessibility/ContrastGuard";
import { ANIMATION } from "../../tokens/designConstants";

// Meta-engine data types
interface SystemUsageMetric {
  systemId: string;
  componentType: string;
  usageCount: number;
  performanceMetrics: {
    renderTime: number;
    memoryUsage: number;
    interactionLatency: number;
    errorRate: number;
  };
  userSatisfactionScore: number;
  timestamp: number;
  context: {
    deviceType: "mobile" | "tablet" | "desktop";
    browserEngine: string;
    networkCondition: "fast" | "moderate" | "slow";
    batteryLevel?: number;
  };
}

interface SystemOptimization {
  id: string;
  targetSystem: string;
  optimizationType:
    | "performance"
    | "memory"
    | "battery"
    | "network"
    | "accessibility"
    | "user-experience";
  description: string;
  impact: number;
  confidence: number;
  implementation: string;
  estimatedGain: {
    performance?: string;
    memory?: string;
    battery?: string;
    userExperience?: string;
  };
  priority: "low" | "medium" | "high" | "critical";
  autoApplyable: boolean;
}

interface SystemEvolution {
  id: string;
  evolutionType:
    | "feature-enhancement"
    | "performance-boost"
    | "adaptive-learning"
    | "emergent-behavior";
  description: string;
  affectedSystems: string[];
  evolutionStage:
    | "detected"
    | "analyzing"
    | "testing"
    | "implementing"
    | "complete";
  confidence: number;
  potentialImpact: number;
}

interface GlassSystemHealth {
  systemId: string;
  healthScore: number;
  issues: Array<{
    type:
      | "performance"
      | "memory-leak"
      | "accessibility"
      | "user-friction"
      | "compatibility";
    severity: "low" | "medium" | "high" | "critical";
    description: string;
    autoFixable: boolean;
  }>;
  recommendations: string[];
  trend: "improving" | "stable" | "declining";
}

type GlassHealthIssue = GlassSystemHealth["issues"][number];
type SystemOptimizationType = SystemOptimization["optimizationType"];

// Advanced quantum-inspired optimization algorithm
class QuantumOptimizer {
  private quantumStates: Map<string, number[]>;
  private entanglementMatrix: number[][];
  private observationHistory: Array<{ state: string; outcome: number }>;

  constructor() {
    this.quantumStates = new Map();
    this.entanglementMatrix = [];
    this.observationHistory = [];
  }

  createQuantumState(systemId: string, dimensions: number): void {
    // Initialize quantum state as probability amplitudes
    const state = Array.from({ length: dimensions }, () => Math.random() - 0.5);
    const norm = Math.sqrt(state.reduce((sum, val) => sum + val * val, 0));
    this.quantumStates.set(
      systemId,
      state.map((val) => val / norm)
    );
  }

  entangleSystems(system1: string, system2: string, strength: number): void {
    const state1 = this.quantumStates.get(system1);
    const state2 = this.quantumStates.get(system2);

    if (state1 && state2) {
      // Simulate quantum entanglement through correlated state updates
      for (let i = 0; i < Math.min(state1.length, state2.length); i++) {
        const correlation = strength * (Math.random() - 0.5);
        state1[i] += correlation * state2[i];
        state2[i] += correlation * state1[i];
      }
    }
  }

  observe(systemId: string): number {
    const state = this.quantumStates.get(systemId);
    if (!state) return 0;

    // Collapse quantum state to measured value
    const probabilities = state.map((amplitude) => amplitude * amplitude);
    const random = Math.random();
    let cumulative = 0;

    for (let i = 0; i < probabilities.length; i++) {
      cumulative += probabilities[i];
      if (random < cumulative) {
        this.observationHistory.push({
          state: systemId,
          outcome: i / probabilities.length,
        });
        return i / probabilities.length;
      }
    }

    return 0;
  }

  evolveStates(): void {
    // Quantum evolution based on Schrödinger equation simulation
    this.quantumStates.forEach((state, systemId) => {
      const evolutionMatrix = this.generateEvolutionMatrix(state.length);
      const newState = this.applyMatrix(evolutionMatrix, state);
      this.quantumStates.set(systemId, newState);
    });
  }

  private generateEvolutionMatrix(size: number): number[][] {
    const matrix = Array.from({ length: size }, () => Array(size).fill(0));

    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        matrix[i][j] =
          Math.cos(0.1 * (i - j)) * Math.exp(-0.01 * (i - j) * (i - j));
      }
    }

    return matrix;
  }

  private applyMatrix(matrix: number[][], state: number[]): number[] {
    return matrix.map((row) =>
      row.reduce((sum, val, idx) => sum + val * state[idx], 0)
    );
  }
}

// Self-healing neural network for system optimization
class SelfHealingNetwork {
  private topology: number[];
  private weights: number[][][];
  private biases: number[][];
  private healthScores: number[];
  private redundantNodes: Map<string, number[]>;

  constructor(topology: number[]) {
    this.topology = topology;
    this.weights = [];
    this.biases = [];
    this.healthScores = [];
    this.redundantNodes = new Map();

    this.initializeNetwork();
  }

  private initializeNetwork(): void {
    for (let i = 0; i < this.topology.length - 1; i++) {
      const layerWeights = Array.from({ length: this.topology[i + 1] }, () =>
        Array.from({ length: this.topology[i] }, () => Math.random() * 2 - 1)
      );
      this.weights.push(layerWeights);

      const layerBiases = Array.from(
        { length: this.topology[i + 1] },
        () => Math.random() * 2 - 1
      );
      this.biases.push(layerBiases);

      this.healthScores.push(...Array(this.topology[i + 1]).fill(1.0));
    }
  }

  forward(input: number[]): number[] {
    let activation = input;

    for (let layer = 0; layer < this.weights.length; layer++) {
      const newActivation = [];

      for (let node = 0; node < this.weights[layer].length; node++) {
        let sum = this.biases[layer][node];

        for (let prevNode = 0; prevNode < activation.length; prevNode++) {
          sum += this.weights[layer][node][prevNode] * activation[prevNode];
        }

        // Apply health-adjusted activation
        const healthFactor =
          this.healthScores[layer * this.topology[layer] + node] || 1.0;
        newActivation.push(this.relu(sum) * healthFactor);
      }

      activation = newActivation;
    }

    return activation;
  }

  private relu(x: number): number {
    return Math.max(0, x);
  }

  diagnoseHealth(): void {
    // Monitor for degraded nodes
    this.healthScores = this.healthScores.map((health, index) => {
      const degradation = Math.random() * 0.01; // Simulate gradual degradation
      return Math.max(0.1, health - degradation);
    });
  }

  healNetwork(): void {
    // Replace unhealthy nodes with redundant ones
    this.healthScores.forEach((health, index) => {
      if (health < 0.5) {
        this.regenerateNode(index);
      }
    });
  }

  private regenerateNode(nodeIndex: number): void {
    const layerIndex = Math.floor(
      nodeIndex / this.topology.reduce((sum, size) => sum + size, 0)
    );
    const nodeInLayer = nodeIndex % this.topology[layerIndex];

    // Reinitialize weights and biases for the damaged node
    if (this.weights[layerIndex] && this.weights[layerIndex][nodeInLayer]) {
      this.weights[layerIndex][nodeInLayer] = this.weights[layerIndex][
        nodeInLayer
      ].map(() => Math.random() * 2 - 1);
    }

    if (
      this.biases[layerIndex] &&
      this.biases[layerIndex][nodeInLayer] !== undefined
    ) {
      this.biases[layerIndex][nodeInLayer] = Math.random() * 2 - 1;
    }

    this.healthScores[nodeIndex] = 1.0;
  }
}

// Main meta-engine class
class GlassMetaEngineCore {
  private systemMetrics: Map<string, SystemUsageMetric[]>;
  private optimizations: SystemOptimization[];
  private evolutions: SystemEvolution[];
  private systemHealth: Map<string, GlassSystemHealth>;
  private quantumOptimizer: QuantumOptimizer;
  private selfHealingNetwork: SelfHealingNetwork;
  private evolutionStage: number;
  private learningRate: number;

  constructor() {
    this.systemMetrics = new Map();
    this.optimizations = [];
    this.evolutions = [];
    this.systemHealth = new Map();
    this.quantumOptimizer = new QuantumOptimizer();
    this.selfHealingNetwork = new SelfHealingNetwork([20, 40, 20, 10]);
    this.evolutionStage = 0;
    this.learningRate = 0.001;

    this.initializeEvolution();
    this.startEvolutionCycle();
  }

  private initializeEvolution(): void {
    // Initialize quantum states for known glass systems
    const glassSystems = [
      "GlassPhysicsEngine",
      "GlassMorphingEngine",
      "Glass3DEngine",
      "OrganicAnimationEngine",
      "EmotionalIntelligenceEngine",
      "SpatialComputingEngine",
      "AIPersonalizationEngine",
    ];

    glassSystems.forEach((system) => {
      this.quantumOptimizer.createQuantumState(system, 8);
      this.systemHealth.set(system, {
        systemId: system,
        healthScore: 1.0,
        issues: [],
        recommendations: [],
        trend: "stable",
      });
    });

    // Create quantum entanglements between related systems
    this.quantumOptimizer.entangleSystems(
      "GlassPhysicsEngine",
      "Glass3DEngine",
      0.7
    );
    this.quantumOptimizer.entangleSystems(
      "EmotionalIntelligenceEngine",
      "OrganicAnimationEngine",
      0.8
    );
    this.quantumOptimizer.entangleSystems(
      "SpatialComputingEngine",
      "Glass3DEngine",
      0.6
    );
  }

  private startEvolutionCycle(): void {
    // Continuous evolution cycle
    setInterval(() => {
      this.evolveSystem();
    }, ANIMATION.DURATION.slower); // Every 5 seconds

    // Health monitoring cycle
    setInterval(() => {
      this.monitorSystemHealth();
    }, ANIMATION.DURATION.slower * 2); // Every 10 seconds

    // Neural network healing
    setInterval(() => {
      this.selfHealingNetwork.diagnoseHealth();
      this.selfHealingNetwork.healNetwork();
    }, ANIMATION.DURATION.slower * 6); // Every 30 seconds
  }

  recordSystemUsage(metric: SystemUsageMetric): void {
    if (!this.systemMetrics.has(metric.systemId)) {
      this.systemMetrics.set(metric.systemId, []);
    }

    const systemMetrics = this.systemMetrics.get(metric.systemId)!;
    systemMetrics.push(metric);

    // Keep only recent metrics (last 1000)
    if (systemMetrics.length > 1000) {
      systemMetrics.splice(0, systemMetrics.length - 1000);
    }

    this.analyzeMetrics(metric.systemId);
    this.generateOptimizations(metric.systemId);
  }

  private analyzeMetrics(systemId: string): void {
    const metrics = this.systemMetrics.get(systemId);
    if (!metrics || metrics.length < 10) return;

    const recent = metrics.slice(-50);
    const avgPerformance =
      recent.reduce((sum, m) => sum + m.performanceMetrics.renderTime, 0) /
      recent.length;
    const avgMemory =
      recent.reduce((sum, m) => sum + m.performanceMetrics.memoryUsage, 0) /
      recent.length;
    const avgSatisfaction =
      recent.reduce((sum, m) => sum + m.userSatisfactionScore, 0) /
      recent.length;

    // Update system health
    const health = this.systemHealth.get(systemId);
    if (health) {
      const newHealthScore = this.calculateHealthScore(
        avgPerformance,
        avgMemory,
        avgSatisfaction
      );
      health.healthScore = newHealthScore;
      health.trend =
        newHealthScore > health.healthScore
          ? "improving"
          : newHealthScore < health.healthScore
            ? "declining"
            : "stable";

      // Identify issues
      if (avgPerformance > 100) {
        health.issues.push({
          type: "performance",
          severity: avgPerformance > 200 ? "high" : "medium",
          description: `Average render time is ${avgPerformance.toFixed(1)}ms`,
          autoFixable: true,
        });
      }

      if (avgMemory > 50) {
        health.issues.push({
          type: "memory-leak",
          severity: avgMemory > 100 ? "critical" : "medium",
          description: `High memory usage detected: ${avgMemory.toFixed(1)}MB`,
          autoFixable: false,
        });
      }

      if (avgSatisfaction < 0.7) {
        health.issues.push({
          type: "user-friction",
          severity: avgSatisfaction < 0.5 ? "high" : "medium",
          description: `User satisfaction below threshold: ${(avgSatisfaction * 100).toFixed(1)}%`,
          autoFixable: true,
        });
      }
    }
  }

  private calculateHealthScore(
    performance: number,
    memory: number,
    satisfaction: number
  ): number {
    const perfScore = Math.max(0, 1 - performance / 200);
    const memScore = Math.max(0, 1 - memory / 100);
    const satScore = satisfaction;

    return perfScore * 0.3 + memScore * 0.2 + satScore * 0.5;
  }

  private generateOptimizations(systemId: string): void {
    const health = this.systemHealth.get(systemId);
    if (!health || health.healthScore > 0.8) return;

    // Generate optimization based on identified issues
    health.issues.forEach((issue) => {
      const optimization = this.createOptimization(systemId, issue);
      if (optimization) {
        this.optimizations.push(optimization);
      }
    });

    // Use neural network to predict optimal configurations
    const metrics = this.systemMetrics.get(systemId);
    if (metrics && metrics.length > 20) {
      const input = this.metricsToVector(metrics.slice(-10));
      const neuralOutput = this.selfHealingNetwork.forward(input);

      const aiOptimization = this.interpretNeuralOutput(systemId, neuralOutput);
      if (aiOptimization) {
        this.optimizations.push(aiOptimization);
      }
    }
  }

  private createOptimization(
    systemId: string,
    issue: GlassHealthIssue
  ): SystemOptimization | null {
    const optimizationId = `opt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    switch (issue.type) {
      case "performance":
        return {
          id: optimizationId,
          targetSystem: systemId,
          optimizationType: "performance",
          description: "Implement render optimization and frame batching",
          impact: 0.8,
          confidence: 0.9,
          implementation:
            'enableFrameBatching: true, renderOptimization: "aggressive"',
          estimatedGain: {
            performance: "40-60% faster rendering",
            userExperience: "Smoother animations",
          },
          priority: "high",
          autoApplyable: true,
        };

      case "memory-leak":
        return {
          id: optimizationId,
          targetSystem: systemId,
          optimizationType: "memory",
          description:
            "Enable garbage collection optimization and memory pooling",
          impact: 0.7,
          confidence: 0.8,
          implementation: 'memoryPooling: true, gcOptimization: "aggressive"',
          estimatedGain: {
            memory: "30-50% reduction in memory usage",
            performance: "Reduced GC pauses",
          },
          priority: "medium",
          autoApplyable: false,
        };

      case "user-friction":
        return {
          id: optimizationId,
          targetSystem: systemId,
          optimizationType: "user-experience",
          description:
            "Adaptive UI optimization based on user behavior patterns",
          impact: 0.6,
          confidence: 0.75,
          implementation: 'adaptiveUI: true, behaviorLearning: "enhanced"',
          estimatedGain: {
            userExperience: "Improved interaction flow",
            performance: "Predictive loading",
          },
          priority: "medium",
          autoApplyable: true,
        };
    }

    return null;
  }

  private interpretNeuralOutput(
    systemId: string,
    output: number[]
  ): SystemOptimization | null {
    const maxIndex = output.indexOf(Math.max(...output));
    const confidence = output[maxIndex];

    if (confidence < 0.6) return null;

    const optimizationTypes: SystemOptimizationType[] = [
      "performance",
      "memory",
      "battery",
      "network",
      "accessibility",
    ];
    const type = optimizationTypes[maxIndex % optimizationTypes.length];

    return {
      id: `neural_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      targetSystem: systemId,
      optimizationType: type,
      description: `AI-suggested ${type} optimization`,
      impact: confidence,
      confidence,
      implementation: `neuralOptimization: {type: "${type}", strength: ${confidence.toFixed(2)}}`,
      estimatedGain: {
        [type]: `${(confidence * 50).toFixed(0)}% improvement expected`,
      },
      priority: confidence > 0.8 ? "high" : "medium",
      autoApplyable: confidence > 0.9,
    };
  }

  private metricsToVector(metrics: SystemUsageMetric[]): number[] {
    const vector = new Array(20).fill(0);

    metrics.forEach((metric, index) => {
      if (index < 5) {
        vector[index * 4] = metric.performanceMetrics.renderTime / 200;
        vector[index * 4 + 1] = metric.performanceMetrics.memoryUsage / 100;
        vector[index * 4 + 2] =
          metric.performanceMetrics.interactionLatency / 50;
        vector[index * 4 + 3] = metric.userSatisfactionScore;
      }
    });

    return vector;
  }

  private evolveSystem(): void {
    this.evolutionStage++;

    // Quantum evolution
    this.quantumOptimizer.evolveStates();

    // Analyze system correlations for emergent behaviors
    this.detectEmergentBehaviors();

    // Apply successful optimizations
    this.applyOptimizations();

    // Generate system evolutions
    this.generateEvolutions();
  }

  private detectEmergentBehaviors(): void {
    const systems = Array.from(this.systemMetrics.keys());

    for (let i = 0; i < systems.length; i++) {
      for (let j = i + 1; j < systems.length; j++) {
        const correlation = this.calculateSystemCorrelation(
          systems[i],
          systems[j]
        );

        if (correlation > 0.8) {
          const evolution: SystemEvolution = {
            id: `emergence_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            evolutionType: "emergent-behavior",
            description: `Strong correlation detected between ${systems[i]} and ${systems[j]}`,
            affectedSystems: [systems[i], systems[j]],
            evolutionStage: "detected",
            confidence: correlation,
            potentialImpact: 0.7,
          };

          this.evolutions.push(evolution);
        }
      }
    }
  }

  private calculateSystemCorrelation(system1: string, system2: string): number {
    const metrics1 = this.systemMetrics.get(system1)?.slice(-20);
    const metrics2 = this.systemMetrics.get(system2)?.slice(-20);

    if (!metrics1 || !metrics2 || metrics1.length < 10 || metrics2.length < 10)
      return 0;

    // Simple correlation calculation
    const perf1 = metrics1.map((m) => m.performanceMetrics.renderTime);
    const perf2 = metrics2.map((m) => m.performanceMetrics.renderTime);

    const mean1 = perf1.reduce((sum, val) => sum + val, 0) / perf1.length;
    const mean2 = perf2.reduce((sum, val) => sum + val, 0) / perf2.length;

    let numerator = 0;
    let denom1 = 0;
    let denom2 = 0;

    for (let i = 0; i < Math.min(perf1.length, perf2.length); i++) {
      const diff1 = perf1[i] - mean1;
      const diff2 = perf2[i] - mean2;
      numerator += diff1 * diff2;
      denom1 += diff1 * diff1;
      denom2 += diff2 * diff2;
    }

    const correlation = numerator / Math.sqrt(denom1 * denom2);
    return Math.abs(correlation);
  }

  private applyOptimizations(): void {
    const autoApplyable = this.optimizations.filter(
      (opt) => opt.autoApplyable && opt.confidence > 0.8
    );

    autoApplyable.forEach((optimization) => {
      // Simulate applying optimization
      // Update system health after optimization
      const health = this.systemHealth.get(optimization.targetSystem);
      if (health) {
        health.healthScore = Math.min(
          1.0,
          health.healthScore + optimization.impact * 0.1
        );
        health.issues = health.issues.filter(
          (issue) =>
            issue.type !== optimization.optimizationType || !issue.autoFixable
        );
      }
    });

    // Remove applied optimizations
    this.optimizations = this.optimizations.filter(
      (opt) => !autoApplyable.includes(opt)
    );
  }

  private generateEvolutions(): void {
    if (this.evolutionStage % 10 === 0) {
      // Every 10 evolution cycles
      const evolution: SystemEvolution = {
        id: `evo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        evolutionType: "adaptive-learning",
        description: "Enhanced learning algorithm deployment",
        affectedSystems: Array.from(this.systemMetrics.keys()),
        evolutionStage: "detected",
        confidence: 0.75 + Math.random() * 0.2,
        potentialImpact: 0.6 + Math.random() * 0.3,
      };

      this.evolutions.push(evolution);
    }
  }

  private monitorSystemHealth(): void {
    this.systemHealth.forEach((health, systemId) => {
      // Auto-heal critical issues
      const criticalIssues = health.issues.filter(
        (issue) => issue.severity === "critical"
      );

      if (criticalIssues.length > 0) {
        criticalIssues.forEach((issue) => {
          if (issue.autoFixable) {
            // Apply emergency healing
            health.healthScore = Math.max(health.healthScore, 0.5);
          }
        });
      }
    });
  }

  // Public API
  getSystemMetrics(): Map<string, SystemUsageMetric[]> {
    return new Map(this.systemMetrics);
  }

  getOptimizations(): SystemOptimization[] {
    return [...this.optimizations];
  }

  getEvolutions(): SystemEvolution[] {
    return [...this.evolutions];
  }

  getSystemHealth(): Map<string, GlassSystemHealth> {
    return new Map(this.systemHealth);
  }

  getQuantumStates(): Array<{ system: string; coherence: number }> {
    return Array.from(this.systemHealth.keys()).map((system) => ({
      system,
      coherence: this.quantumOptimizer.observe(system),
    }));
  }
}

// React Context for the meta-engine
const GlassMetaEngineContext = createContext<{
  engine: GlassMetaEngineCore | null;
  recordUsage: (metric: SystemUsageMetric) => void;
  optimizations: SystemOptimization[];
  evolutions: SystemEvolution[];
  systemHealth: Map<string, GlassSystemHealth>;
}>({
  engine: null,
  recordUsage: () => {},
  optimizations: [],
  evolutions: [],
  systemHealth: new Map(),
});

// Provider component
export function GlassMetaEngineProvider({
  children,
  onEvolution,
  onOptimization,
}: {
  children: React.ReactNode;
  onEvolution?: (evolution: SystemEvolution) => void;
  onOptimization?: (optimization: SystemOptimization) => void;
}) {
  const prefersReducedMotion = useReducedMotion();
  const engineRef = useRef<GlassMetaEngineCore>();
  if (!engineRef.current) engineRef.current = new GlassMetaEngineCore();
  const [optimizations, setOptimizations] = useState<SystemOptimization[]>([]);
  const [evolutions, setEvolutions] = useState<SystemEvolution[]>([]);
  const [systemHealth, setSystemHealth] = useState<
    Map<string, GlassSystemHealth>
  >(() => new Map(engineRef.current?.getSystemHealth() ?? []));

  // Initialize engine
  useEffect(() => {
    // Update state periodically
    const updateInterval = setInterval(() => {
      if (engineRef.current) {
        const newOptimizations = engineRef.current.getOptimizations();
        const newEvolutions = engineRef.current.getEvolutions();
        const newHealth = engineRef.current.getSystemHealth();

        setOptimizations(newOptimizations);
        setEvolutions(newEvolutions);
        setSystemHealth(newHealth);

        // Trigger callbacks
        newOptimizations.forEach((opt) => onOptimization?.(opt));
        newEvolutions.forEach((evo) => onEvolution?.(evo));
      }
    }, ANIMATION.DURATION.fast);

    return () => clearInterval(updateInterval);
  }, [onEvolution, onOptimization]);

  const recordUsage = useCallback((metric: SystemUsageMetric) => {
    engineRef.current?.recordSystemUsage(metric);
  }, []);

  const value = {
    engine: engineRef.current || null,
    recordUsage,
    optimizations,
    evolutions,
    systemHealth,
  };

  return (
    <GlassMetaEngineContext.Provider value={value}>
      {children}
    </GlassMetaEngineContext.Provider>
  );
}

// Hook to use the meta-engine
export function useGlassMetaEngine() {
  const context = useContext(GlassMetaEngineContext);
  if (!context) {
    throw new Error(
      "useGlassMetaEngine must be used within GlassMetaEngineProvider"
    );
  }
  return context;
}

// Export GlassMetaEngine as an alias for GlassMetaDashboard for backward compatibility
export const GlassMetaEngine = GlassMetaDashboard;

// Meta-engine dashboard component
export function GlassMetaDashboard({
  className,
  showQuantumStates = true,
  showEvolutions = true,
  maxOptimizations = 10,
  defaultOpen = false,
  inline = false,
}: {
  className?: string;
  showQuantumStates?: boolean;
  showEvolutions?: boolean;
  maxOptimizations?: number;
  /** Render the substantive dashboard immediately without requiring interaction. */
  defaultOpen?: boolean;
  /** Keep the dashboard in document flow for embedded and responsive surfaces. */
  inline?: boolean;
}) {
  const prefersReducedMotion = useReducedMotion();
  const { optimizations, evolutions, systemHealth, engine } =
    useGlassMetaEngine();
  const [showDashboard, setShowDashboard] = useState(defaultOpen);
  const [quantumStates, setQuantumStates] = useState<
    Array<{ system: string; coherence: number }>
  >([]);

  // Update quantum states
  useEffect(() => {
    if (engine && showQuantumStates) {
      const updateStates = () => {
        setQuantumStates(engine.getQuantumStates());
      };

      updateStates();
      const interval = setInterval(updateStates, ANIMATION.DURATION.normal);
      return () => clearInterval(interval);
    }
  }, [engine, showQuantumStates]);

  const topOptimizations = optimizations.slice(0, maxOptimizations);
  const criticalEvolutions = evolutions.filter((evo) => evo.confidence > 0.8);
  const healthEntries = Array.from(systemHealth.entries());
  const overview: Array<[string, GlassSystemHealth]> = healthEntries.length
    ? healthEntries.slice(0, 4)
    : [
        ["Rendering", { systemId: "Rendering", healthScore: 0.98, issues: [], recommendations: [], trend: "stable" }],
        ["Accessibility", { systemId: "Accessibility", healthScore: 1, issues: [], recommendations: [], trend: "stable" }],
        ["Motion", { systemId: "Motion", healthScore: 0.96, issues: [], recommendations: [], trend: "stable" }],
      ];
  const healthLabel = (systemId: string) => {
    const normalized = systemId.replace("Glass", "").replace("Engine", "");
    const labels: Record<string, string> = {
      Physics: "Material physics",
      Morphing: "Shape transitions",
      "3D": "Spatial rendering",
      OrganicAnimation: "Organic motion",
    };
    return labels[normalized] ?? normalized.replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  return (
    <div
      className={cn(
        inline
          ? "relative glass-w-full glass-meta-dashboard-inline"
          : "fixed bottom-4 right-4 z-50",
        "glass-on-light",
        className
      )}
      data-glass-component
    >
      {/* The launcher is reserved for the floating application mode. */}
      {!inline && <motion.button
        className={cn(
          "glass-radius-full glass-surface-primary glass-elev-4 glass-border glass-border-white/30",
          "flex items-center justify-center glass-text-primary",
          "transition-all",
          { transitionDuration: "var(--glass-motion-duration-normal)" },
          "relative overflow-hidden"
        )}
        style={{ width: 56, height: 56, minWidth: 56, minHeight: 56 }}
        onClick={() => setShowDashboard(!showDashboard)}
        whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
        whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
        transition={{ duration: ANIMATION.DURATION.fast / 1000 }}
        aria-label="Toggle Meta-Engine dashboard"
        aria-expanded={showDashboard}
      >
        <div className="glass-absolute glass-inset-0 glass-opacity-20" aria-hidden="true">
          {quantumStates.map((state, index) => (
            <motion.div
              key={state.system}
              className="glass-absolute glass-w-1 glass-h-1 glass-surface-secondary glass-radius-full"
              animate={
                prefersReducedMotion
                  ? {}
                  : {
                      x: Math.cos(index * 0.8) * 20 + 20,
                      y: Math.sin(index * 0.8) * 20 + 20,
                      opacity: state.coherence,
                    }
              }
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      duration: ANIMATION.DURATION.slower / 1000,
                      repeat: Infinity,
                      ease: ANIMATION.EASING.linear,
                    }
              }
            />
          ))}
        </div>

        <div className="glass-relative glass-z-10 glass-font-semibold" aria-hidden="true">
          ◈
          {optimizations.length > 0 && (
            <motion.div
              className="glass-absolute glass--top-2 glass--right-2 glass-w-4 glass-h-4 glass-surface-secondary glass-radius-full glass-text-xs glass-text-primary glass-flex glass-items-center glass-justify-center"
              initial={{ scale: 0 }}
              animate={prefersReducedMotion ? {} : { scale: 1 }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { duration: ANIMATION.DURATION.normal / 1000 }
              }
            >
              {optimizations.length}
            </motion.div>
          )}
        </div>
      </motion.button>}

      {/* Meta-engine dashboard */}
      <AnimatePresence>
        {showDashboard && (
          <motion.div
            className={cn(
              inline
                ? "relative glass-mt-4 glass-w-full"
                : "absolute right-0 glass-max-h-80vh overflow-y-auto",
              "glass-surface-primary glass-elev-5 glass-radius-2xl glass-p-6 glass-border glass-border-white/30"
            )}
            style={{
              bottom: inline ? undefined : "calc(100% + 12px)",
              width: inline ? "100%" : "min(24rem, calc(100vw - 2rem))",
              boxSizing: "border-box",
              maxHeight: inline ? undefined : "min(80vh, 720px)",
            }}
            initial={false}
            animate={prefersReducedMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { duration: ANIMATION.DURATION.normal / 1000 }
            }
            role="dialog"
            aria-label="Meta-Engine dashboard"
          >
            <ContrastGuard className="glass-block">
              <div className="glass-flex glass-items-start glass-justify-between glass-gap-4 glass-mb-5">
                <div>
                  <div className="glass-text-xs glass-font-semibold glass-text-secondary glass-uppercase glass-tracking-wide">
                    Adaptive runtime
                  </div>
                  <h3 className="glass-text-xl glass-font-semibold glass-text-primary glass-mt-1">
                    System Intelligence
                  </h3>
                  <p className="glass-text-sm glass-text-secondary glass-mt-1">
                    Live material, motion, and accessibility health.
                  </p>
                </div>
                {!inline && <button
                  onClick={() => setShowDashboard(false)}
                  className="glass-text-sm glass-text-secondary hover:glass-text-primary glass-focus glass-touch-target"
                  aria-label="Close Meta-Engine dashboard"
                >
                  <span aria-hidden="true">×</span>
                </button>}
              </div>

              {/* System Health Overview */}
              <div className="glass-gap-2 glass-mb-5">
                <h4 className="glass-text-sm glass-font-medium glass-text-secondary glass-uppercase glass-tracking-wide">
                  System health
                </h4>
                <div
                  className="glass-grid glass-gap-2"
                  style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))" }}
                >
                  {overview.map(
                    ([systemId, health]) => (
                      <motion.div
                        key={systemId}
                        className="glass-p-4 glass-surface-secondary glass-radius-xl glass-border glass-border-white/20"
                        initial={false}
                        animate={
                          prefersReducedMotion ? {} : { opacity: 1, x: 0 }
                        }
                      >
                        <div className="glass-flex glass-items-center glass-justify-between">
                          <span className="glass-text-sm glass-text-primary glass-font-medium glass-min-glass-w-0">
                            {healthLabel(String(systemId))}
                          </span>
                          <div className="glass-flex glass-items-center glass-gap-2">
                            <div className="glass-w-2 glass-h-2 glass-radius-full glass-surface-secondary glass-border glass-border-white/30" />
                            <span className="glass-text-xs glass-text-secondary">
                              {(health.healthScore * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        {health.issues.length > 0 && (
                          <div className="glass-mt-1 glass-text-xs glass-text-tertiary">
                            {health.issues.length} issue
                            {health.issues.length !== 1 ? "s" : ""} detected
                          </div>
                        )}
                      </motion.div>
                    )
                  )}
                </div>
              </div>

              {/* Quantum States */}
              {showQuantumStates && quantumStates.length > 0 && (
                <div className="glass-gap-2">
                  <h4 className="glass-text-sm glass-font-medium glass-text-secondary glass-uppercase glass-tracking-wide">
                    Quantum Coherence
                  </h4>
                  <div className="glass-grid glass-grid-cols-2 glass-gap-2">
                    {quantumStates.map((state) => (
                      <div
                        key={state.system}
                        className="glass-p-2 glass-surface-secondary glass-radius-sm"
                      >
                        <div className="glass-text-xs glass-text-primary glass-font-medium">
                          {state.system.replace("Glass", "")}
                        </div>
                        <div className="glass-mt-1 glass-flex glass-items-center glass-gap-2">
                          <div className="glass-flex-1 glass-surface-subtle glass-radius-full glass-h-2">
                            <motion.div
                              className="glass-h-2 glass-surface-blue glass-radius-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${state.coherence * 100}%` }}
                              transition={
                                prefersReducedMotion
                                  ? { duration: 0 }
                                  : { duration: ANIMATION.DURATION.slow / 1000 }
                              }
                            />
                          </div>
                          <span className="glass-text-xs glass-text-secondary">
                            {(state.coherence * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Optimizations */}
              {topOptimizations.length > 0 && (
                <div className="glass-gap-2">
                  <h4 className="glass-text-sm glass-font-medium glass-text-secondary glass-uppercase glass-tracking-wide">
                    Active Optimizations
                  </h4>
                  {topOptimizations.map((optimization) => (
                    <motion.div
                      key={optimization.id}
                      className="glass-p-3 glass-surface-secondary glass-radius-md"
                      initial={false}
                      animate={prefersReducedMotion ? {} : { opacity: 1, x: 0 }}
                    >
                      <div className="glass-flex glass-items-center glass-justify-between">
                        <span className="glass-text-sm glass-text-primary glass-font-medium">
                          {optimization.targetSystem.replace("Glass", "")}
                        </span>
                        <div className="glass-flex glass-items-center glass-gap-1">
                          <div
                            className="glass-w-2 glass-h-2 glass-radius-full"
                            style={{
                              backgroundColor:
                                optimization.priority === "critical"
                                  ? "var(--glass-color-danger-dark)"
                                  : optimization.priority === "high"
                                    ? "hsl(var(--glass-color-warning))"
                                    : optimization.priority === "medium"
                                      ? "hsl(var(--glass-color-success))"
                                      : "var(--glass-gray-500)",
                            }}
                          />
                          <span className="glass-text-xs glass-text-secondary glass-capitalize">
                            {optimization.priority}
                          </span>
                        </div>
                      </div>
                      <div className="glass-mt-1 glass-text-xs glass-text-tertiary">
                        {optimization.description}
                      </div>
                      <div className="glass-mt-2 glass-flex glass-items-center glass-justify-between">
                        <span className="glass-text-xs glass-text-secondary glass-capitalize">
                          {optimization.optimizationType}
                        </span>
                        <span className="glass-text-xs glass-text-secondary">
                          Impact: {(optimization.impact * 100).toFixed(0)}%
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* System Evolutions */}
              {showEvolutions && criticalEvolutions.length > 0 && (
                <div className="glass-gap-2">
                  <h4 className="glass-text-sm glass-font-medium glass-text-secondary glass-uppercase glass-tracking-wide">
                    System Evolutions
                  </h4>
                  {criticalEvolutions.slice(0, 3).map((evolution) => (
                    <motion.div
                      key={evolution.id}
                      className="glass-p-3 glass-surface-secondary glass-radius-md"
                      initial={false}
                      animate={prefersReducedMotion ? {} : { opacity: 1, x: 0 }}
                    >
                      <div className="glass-text-sm glass-text-primary glass-font-medium">
                        {evolution.evolutionType.replace("-", " ")}
                      </div>
                      <div className="glass-mt-1 glass-text-xs glass-text-tertiary">
                        {evolution.description}
                      </div>
                      <div className="glass-mt-2 glass-flex glass-items-center glass-justify-between">
                        <span className="glass-text-xs glass-text-secondary glass-capitalize">
                          {evolution.evolutionStage}
                        </span>
                        <span className="glass-text-xs glass-text-secondary">
                          {(evolution.confidence * 100).toFixed(0)}% confidence
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {optimizations.length === 0 && evolutions.length === 0 && (
                <div className="glass-surface-secondary glass-radius-xl glass-border glass-border-white/20 glass-p-4">
                  <div className="glass-text-sm glass-font-medium glass-text-primary">
                    All systems nominal
                  </div>
                  <div className="glass-mt-1 glass-text-sm glass-text-secondary">
                    Continuous analysis is active. No intervention is required.
                  </div>
                </div>
              )}
            </ContrastGuard>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Hook to automatically record system usage
export function useMetaEngineRecorder(systemId: string, componentType: string) {
  const { recordUsage } = useGlassMetaEngine();
  const startTimeRef = useRef<number>(Date.now());

  const recordMetric = useCallback(
    (userSatisfactionScore: number = 1.0) => {
      const endTime = Date.now();
      const renderTime = endTime - startTimeRef.current;
      const runtimePerformance = globalThis.performance as Performance & {
        memory?: { usedJSHeapSize: number };
      };
      const navigatorWithConnection = navigator as Navigator & {
        connection?: { effectiveType?: string };
      };
      const effectiveType =
        navigatorWithConnection.connection?.effectiveType || "unknown";
      const networkCondition: SystemUsageMetric["context"]["networkCondition"] =
        effectiveType.includes("2g")
          ? "slow"
          : effectiveType.includes("3g")
            ? "moderate"
            : "fast";

      recordUsage({
        systemId,
        componentType,
        usageCount: 1,
        performanceMetrics: {
          renderTime,
          memoryUsage:
            (runtimePerformance.memory?.usedJSHeapSize ?? 0) / 1024 / 1024,
          interactionLatency: 0,
          errorRate: 0,
        },
        userSatisfactionScore,
        timestamp: Date.now(),
        context: {
          deviceType:
            window.innerWidth < 768
              ? "mobile"
              : window.innerWidth < 1024
                ? "tablet"
                : "desktop",
          browserEngine: navigator.userAgent.includes("Chrome")
            ? "Chromium"
            : navigator.userAgent.includes("Firefox")
              ? "Gecko"
              : navigator.userAgent.includes("Safari")
                ? "WebKit"
                : "Unknown",
          networkCondition,
        },
      });

      startTimeRef.current = Date.now();
    },
    [recordUsage, systemId, componentType]
  );

  // Auto-record on component mount/unmount
  useEffect(() => {
    recordMetric();
    return () => recordMetric();
  }, [recordMetric]);

  return { recordMetric };
}

// Presets for different meta-engine configurations
export const metaEnginePresets = {
  conservative: {
    evolutionRate: 0.1,
    optimizationThreshold: 0.8,
    quantumCoherence: 0.5,
    selfHealingEnabled: false,
  },
  balanced: {
    evolutionRate: 0.5,
    optimizationThreshold: 0.6,
    quantumCoherence: 0.7,
    selfHealingEnabled: true,
  },
  aggressive: {
    evolutionRate: 0.9,
    optimizationThreshold: 0.4,
    quantumCoherence: 0.9,
    selfHealingEnabled: true,
  },
  experimental: {
    evolutionRate: 1.0,
    optimizationThreshold: 0.2,
    quantumCoherence: 1.0,
    selfHealingEnabled: true,
  },
};
